import { Link } from "react-router";

import type { NextThirtyDaysItemResource } from "@/lib/api-types";
import { formatDateCell } from "@/lib/format";

/**
 * "Next 30 days" (Story 13.1, R-H2) — DESIGN.md's own dashboard vocabulary
 * is "stat tiles plus alert rows plus lists" (§ Components — no fourth,
 * dedicated row primitive is named for this panel), so this mirrors `Alert
 * Row`'s own structural shape one level plainer: a sentence at
 * `{typography.body}`, the date/entity meta beneath at `{typography.meta}`,
 * the whole row itself the one working link (AD-27) — no severity stripe
 * (nothing here has crossed a threshold yet, R-H2's own Intent: "approaching
 * within a real calendar window... regardless of whether any alert has
 * fired") and no action buttons (this row is a forward-looking notice, not
 * an open alert instance — acknowledge/snooze stay `AlertRow`'s own job).
 */
export interface NextThirtyDaysRowProps {
  item: NextThirtyDaysItemResource;
}

export function NextThirtyDaysRow({ item }: NextThirtyDaysRowProps) {
  const meta = [item.entity_label, formatDateCell(item.date)].filter((part): part is string => Boolean(part)).join(" · ");

  const content = (
    <>
      <p className="text-body text-ink">{item.sentence}</p>
      <p className="mt-1 text-meta text-ink-secondary">{meta}</p>
    </>
  );

  return (
    <li className="min-h-alert-row border-b border-border-hairline bg-surface">
      {item.entity_url ? (
        <Link
          to={item.entity_url}
          className="block min-h-alert-row px-2 py-3 text-ink no-underline hover:underline"
        >
          {content}
        </Link>
      ) : (
        <div className="min-h-alert-row px-2 py-3">{content}</div>
      )}
    </li>
  );
}
